import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  SimpleGrid,
  Flex,
  Text,
  Switch,
} from "@chakra-ui/react";

import TaskStatus from "../Tasks/TaskStatus";
import BugsSeverity from "../Tasks/BugsSeverity";
import TeamWorkload from "../Tasks/TeamWorkload";
import Alerts from "../Tasks/Alerts";
import MyWorks from "../works/components/MyWorks";
import ProjectOverview from "../works/components/ProjectOverview";
import { IoMdOptions } from "react-icons/io";
import {
  UncontrolledPopover,
  PopoverBody,
} from "reactstrap";

import httpInjectorService from "services/http-injector.service";

/* DND */

import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

/* ---------------- SORTABLE ITEM ---------------- */

const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </Box>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */

const TaskSection = ({ data = {}, loading }) => {
  /* ---------------- CONFIG ---------------- */

  const [config, setConfig] = useState({
    myWorks: true,
    projectOverview: true,
    taskStatus: true,
    bugsSeverity: true,
    teamWorkload: true,
    alerts: true,
  });

  /* ---------------- ORDER ---------------- */

  const [widgetOrder, setWidgetOrder] =
    useState([
      "myWorks",
      "projectOverview",
      "taskStatus",
      "bugsSeverity",
      "teamWorkload",
      "alerts",
    ]);

  /* ---------------- TOGGLE ---------------- */

  const handleToggle = (key) => {
    const newConfig = {
      ...config,
      [key]: !config[key],
    };

    setConfig(newConfig);

    updateVerticalConfig(newConfig);
  };

  /* ---------------- SETTINGS ---------------- */

  const settingsList = [
    { key: "myWorks", label: "My Works" },
    {
      key: "projectOverview",
      label: "Project Overview",
    },
    { key: "taskStatus", label: "Task Status" },
    {
      key: "bugsSeverity",
      label: "Bugs Severity",
    },
    {
      key: "teamWorkload",
      label: "Team Workload",
    },
    { key: "alerts", label: "Alerts" },
  ];

  /* ---------------- FETCH CONFIG ---------------- */

  const getVerticalConfig = async () => {
    try {
      const response =
        await httpInjectorService.getVerticalDashboardConfig();

      if (
        response?.status === "success" &&
        response?.data?.length > 0 &&
        response.data[0]
          ?.vertical_dashboard_config
      ) {
        setConfig(
          response.data[0]
            .vertical_dashboard_config
        );
      }
    } catch (error) {
      console.error(
        "Error fetching vertical config:",
        error
      );
    }
  };

  const updateVerticalConfig = async (
    updatedConfig
  ) => {
    try {
      const response =
        await httpInjectorService.updateVerticalDashboardConfig(
          {
            verticalDashboardConfig:
              updatedConfig,
          }
        );

      if (
        response &&
        response.status === "success"
      ) {
        setConfig(updatedConfig);
      }
    } catch (error) {}
  };

  /* ---------------- SAVE ORDER ---------------- */

  const saveWidgetOrder = async (order) => {
   
    try {
      await httpInjectorService.updateVerticalWidgetSequence(
        {
          widget_sequence: order,
        }
      );
    } catch (error) {
      console.error(error);
    }
  };

  /* ---------------- DRAG ---------------- */

  const handleDragEnd = async (
    event
  ) => {
    const { active, over } = event;

    if (
      !over ||
      active.id === over.id
    )
      return;

    const oldIndex =
      widgetOrder.indexOf(
        active.id
      );

    const newIndex =
      widgetOrder.indexOf(
        over.id
      );

    const newOrder = arrayMove(
      widgetOrder,
      oldIndex,
      newIndex
    );
    setWidgetOrder(newOrder);
    await saveWidgetOrder(
      newOrder
    );
  };

const getSequence = async () => {
  try {
    const response = await httpInjectorService.getVerticalWidgetSequence();

    if (
      response?.status === "success" &&
      Array.isArray(response?.data) &&
      response.data.length > 0 &&
      response.data[0]?.vertical_dashboard_sequence?.length > 0
    ) {
      // Only set when actual sequence data exists
      setWidgetOrder(response.data[0].vertical_dashboard_sequence);
    } else {
      console.log("No widget sequence data found");
    }
  } catch (error) {
    console.error("Error fetching widget sequence:", error);
  }
};


  useEffect(() => {
    getVerticalConfig();
    getSequence();
  }, []);

  /* ---------------- ORDER + FILTER ---------------- */

  const visibleWidgets =
    widgetOrder.filter(
      (key) => config[key]
    );

  /* ---------------- WIDGET MAP ---------------- */

  const widgetMap = {
    myWorks: (
      <Box gridColumn={{ base: "1", lg: "span 1" }}>
        <MyWorks
          data={data}
          loading={loading}
        />
      </Box>
    ),

    projectOverview: (
      <Box gridColumn={{ base: "1", lg: "span 1" }}>
        <ProjectOverview
          data={data}
          loading={loading}
        />
      </Box>
    ),

    taskStatus: (
      <TaskStatus
        data={data}
        loading={loading}
      />
    ),

    bugsSeverity: (
      <BugsSeverity
        data={data}
        loading={loading}
      />
    ),

    teamWorkload: (
      <TeamWorkload
        data={data}
        loading={loading}
      />
    ),

    alerts: <Alerts />,
  };

  /* ---------------- UI ---------------- */

  return (
    <Box>
      <Flex justify="space-between" mb={4} data-tour="task-analytics">
        <h4>Project Stats</h4>

        <Button
          colorScheme="purple"
          id="dashboardSettings"
          size="sm"
        >
         <IoMdOptions size="17px" />
        </Button>

        <UncontrolledPopover
          placement="right"
          target="dashboardSettings"
          trigger="legacy"
        >
          <PopoverBody
            style={{
              width: "220px",
              padding: "10px 5px",
            }}
          >
            <h5
              style={{
                marginBottom: "20px",
                marginLeft: "10px",
                fontWeight: "600",
                fontSize: "18px",
              }}
            >
              Dashboard Settings
            </h5>

            {settingsList.map(
              (item) => (
                <Flex
                  key={item.key}
                  align="center"
                  justify="space-between"
                  mb={2}
                >
                  <Text fontSize="sm">
                    {item.label}
                  </Text>

                  <Switch
                    isChecked={
                      config[item.key]
                    }
                    onChange={() =>
                      handleToggle(
                        item.key
                      )
                    }
                  />
                </Flex>
              )
            )}
          </PopoverBody>
        </UncontrolledPopover>
      </Flex>

      {/* ---------------- DND GRID ---------------- */}

      <DndContext
        collisionDetection={
          closestCenter
        }
        onDragEnd={
          handleDragEnd
        }
      >
        <SortableContext
          items={visibleWidgets}
          strategy={
            verticalListSortingStrategy
          }
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={4}
          >
            {visibleWidgets.map(
              (key) => (
                <SortableItem
                  key={key}
                  id={key}
                >
                  {({
                    attributes,
                    listeners,
                  }) => (
                    <Box
                      cursor="grab"
                      {...attributes}
                      {...listeners}
                    >
                      {
                        widgetMap[
                          key
                        ]
                      }
                    </Box>
                  )}
                </SortableItem>
              )
            )}
          </SimpleGrid>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default TaskSection;